// ============================================================================
// constants.js — the shared vocabulary of Titanlord.
// Every tuning number, enum, and faction/team id lives here so the whole
// codebase speaks one language. Integer enums are used on hot paths.
// World is the XZ plane, +Y up. 1 unit = 1 meter.
// ============================================================================

// ---- Fixed-timestep logic ----
export const TICK_HZ = 60;
export const TICK_DT = 1 / TICK_HZ;          // seconds per logic tick
export const MAX_SUBSTEPS = 5;               // clamp spiral-of-death

// ---- Map ----
export const MAP_SIZE = 400;                 // 400x400 m
export const MAP_HALF = MAP_SIZE / 2;

// ---- Entity pool capacities ----
export const MAX_ENEMIES = 1200;             // grunts + captains + archers (both factions)
export const MAX_HITSPARKS = 512;
export const MAX_DMGNUM = 128;
export const MAX_PICKUPS = 64;
export const MAX_PROJECTILES = 160;          // archer ember bolts (both factions)

// ---- Factions / teams ----
export const TEAM_PLAYER = 0;                // Benjen's host (black & silver)
export const TEAM_MAGMAR = 1;                // Magmar Dynasty (molten red)
export const TEAM_COUNT = 2;

export const TEAM_COLOR = {
  [TEAM_PLAYER]: 0x9aa4b2,   // silver-steel
  [TEAM_MAGMAR]: 0x6b1414,   // dried-blood red
};
export const TEAM_BANNER = {
  [TEAM_PLAYER]: 0x171a1f,   // black banner, silver trim
  [TEAM_MAGMAR]: 0xc23a12,   // molten red
};
export const TEAM_ACCENT = {
  [TEAM_PLAYER]: 0xd7dbe0,   // silver
  [TEAM_MAGMAR]: 0xff6a1a,   // ember orange
};

// ---- Enemy / unit archetypes ----
export const UNIT_FOOTSOLDIER = 0;
export const UNIT_ARCHER = 1;
export const UNIT_CAPTAIN = 2;               // pyre-captain mini-boss
export const UNIT_WARLORD = 3;               // named officer
export const UNIT_TYPE_COUNT = 4;

// Per-archetype base stats. hp, moveSpeed(m/s), atkRange(m), atkDamage,
// atkCooldown(s), aggroRange(m), scale, mass(knockback resistance).
export const UNIT_STATS = {
  [UNIT_FOOTSOLDIER]: { hp: 22,  speed: 3.4, atkRange: 2.0, atkDmg: 5,  atkCd: 1.8, aggro: 22, scale: 1.0,  mass: 1.0 },
  [UNIT_ARCHER]:      { hp: 16,  speed: 2.8, atkRange: 24,  atkDmg: 8,  atkCd: 2.8, aggro: 34, scale: 0.98, mass: 0.9 },
  [UNIT_CAPTAIN]:     { hp: 220, speed: 3.0, atkRange: 2.6, atkDmg: 16, atkCd: 1.9, aggro: 26, scale: 1.35, mass: 3.2 },
  [UNIT_WARLORD]:     { hp: 900, speed: 3.6, atkRange: 3.2, atkDmg: 26, atkCd: 1.5, aggro: 30, scale: 1.55, mass: 6.0 },
};

// ---- Enemy behaviour state machine ----
export const ST_IDLE = 0;
export const ST_SEEK = 1;      // moving toward target
export const ST_ATTACK = 2;    // winding / executing an attack
export const ST_FLINCH = 3;    // small hitstun
export const ST_LAUNCH = 4;    // airborne juggle
export const ST_KNOCK = 5;     // knockback tumble
export const ST_STUN = 6;      // longer stagger, vulnerable
export const ST_DEAD = 7;      // dying / corpse fade
export const ST_BLOCK = 8;     // captains/warlords guarding

// ---- AI level-of-detail bands (distance from player, metres) ----
export const LOD_FULL = 0;     // < 25 m: full AI + limb animation
export const LOD_STEER = 1;    // 25–60 m: simplified steering, cheap anim
export const LOD_STAT = 2;     // > 60 m: statistical / frozen
export const LOD_FULL_R2 = 25 * 25;
export const LOD_STEER_R2 = 60 * 60;

// ---- Reactions (how a hit makes a target respond) ----
export const RE_FLINCH = 0;
export const RE_LAUNCH = 1;
export const RE_KNOCK = 2;
export const RE_STUN = 3;

// ---- Player tuning ----
export const PLAYER_HP_MAX = 500;
export const PLAYER_SPEED = 7.2;             // m/s ground run
export const SPRINT_MULT = 1.7;              // hold Shift to sprint
export const PLAYER_ACCEL = 60;              // m/s^2 toward desired velocity
export const PLAYER_TURN_RATE = 14;          // rad/s facing lerp
export const PLAYER_RADIUS = 0.6;
export const DODGE_SPEED = 15;               // m/s roll burst
export const DODGE_TIME = 0.42;              // s roll duration
export const DODGE_IFRAME_START = 0.04;
export const DODGE_IFRAME_END = 0.32;        // i-frames window within the roll
export const DODGE_COOLDOWN = 0.15;
export const JUMP_V = 9.5;                    // m/s launch (also for aerial combo)
export const GRAVITY = 26;                    // m/s^2

// ---- Combat feel ----
export const HITSTOP_MIN = 0.06;             // 60 ms
export const HITSTOP_MAX = 0.10;             // 100 ms
export const HITSTOP_HEAVY = 0.13;
export const SHAKE_PER_HIT = 0.05;
export const SHAKE_MAX = 1.2;
export const HIT_COUNTER_DECAY = 2.0;        // s of no hits before combo counter resets

// ---- Titan gauge / Titanfall ----
export const TITAN_MAX = 100;
export const TITAN_GAIN_DEAL = 0.9;          // per point of damage dealt
export const TITAN_GAIN_TAKE = 1.8;          // per point of damage taken
export const TITAN_GAIN_KILL = 4.0;          // per kill
export const TITANFALL_DURATION = 3.0;       // s invincible storm
export const TITANFALL_RADIUS = 14;          // m AoE storm reach
export const TITANFALL_TICK = 0.18;          // s between storm damage pulses
export const TITANFALL_DMG = 40;             // per pulse
export const TITANFALL_KNOCK = 22;           // knockback impulse

// ---- Blocking ----
export const BLOCK_ARC = Math.PI * 0.62;     // frontal cone that blocking covers
export const BLOCK_CHIP = 0.25;              // fraction of damage taken through block
export const GUARDBREAK_STUN = 1.4;          // s stunned when guard broken

// ---- Morale ----
// Morale is a "tide" not a body-count: per-kill nudges are tiny and a gentle
// drift pulls it back toward 50, so captures/warlord deaths are what really move
// it. Player kills of Magmar count for more (Benjen is the equaliser).
export const MORALE_MAX = 100;
export const MORALE_START = 50;
export const MORALE_KILL = 0.02;             // ally auto-kills a Magmar grunt
export const MORALE_PLAYER_KILL = 0.05;      // Benjen personally slays a Magmar
export const MORALE_CAPTURE = 9;             // per bastion captured (signed by team)
export const MORALE_WARLORD = 15;            // per warlord slain
export const MORALE_ALLY_LOSS = -0.02;
export const MORALE_DRIFT = 0.05;            // per-second pull back toward 50

// ---- Spatial hash ----
export const HASH_CELL = 4;                  // m per cell — ~ enemy spacing

// ---- Bastions ----
export const BASTION_CAPTURE_RADIUS = 12;

// ---- Rendering / world dressing ----
export const FOG_COLOR = 0x2a2320;
export const FOG_NEAR = 40;
export const FOG_FAR = 240;
export const SKY_TOP = 0x241a16;
export const SKY_BOTTOM = 0x3a2a1e;
export const GROUND_COLOR = 0x2e2823;

// ---- Camera ----
export const CAM_FOV = 60;
export const CAM_DIST = 8.5;                 // default third-person orbit distance
export const CAM_MIN_DIST = 0.0;             // 0 = first-person
export const CAM_MAX_DIST = 22;
export const CAM_PIVOT_H = 1.55;             // orbit pivot height (Benjen's head)
export const CAM_PITCH_DEFAULT = 0.34;       // slight downward tilt
export const CAM_PITCH_MIN = -0.55;          // look up
export const CAM_PITCH_MAX = 1.25;           // look down
export const CAM_POS_STIFF = 20;             // position follow stiffness (snappy, not floaty)
export const CAM_PIVOT_STIFF = 16;           // pivot follow stiffness
export const CAM_LOOK_AHEAD = 1.1;           // gentle lead in the move direction
export const CAM_RECENTER_RATE = 1.6;        // rad/s auto-recenter while moving (gentle)
export const CAM_ZOOM_STEP = 0.012;          // metres per wheel delta unit

// ---- Global draw / spawn budget ----
export const TARGET_ONSCREEN = 240;          // enemies we aim to keep alive near action
